/**
 * The six numbers, the infinity mark that replaces them, and what a screen
 * reader is told about either.
 *
 * ---------------------------------------------------------------------------
 * WHY THE TIMER IS NOT A LIVE REGION
 * ---------------------------------------------------------------------------
 * The instinct is `aria-live="polite"` on the digits, and it makes the page
 * unusable: the seconds change every second, so the screen reader restarts its
 * announcement every second and never finishes one, and no other content on the
 * page can be read at all while the tab is open. `role="timer"` is the right
 * role and it carries an implicit `aria-live="off"` precisely because of this.
 *
 * A separate, visually hidden region is announced instead, and only when the
 * MINUTES change - about the rate at which a countdown six years out is
 * actually news. The digits stay readable on demand, the way a sighted user
 * reads them: by looking, not by being shouted at.
 */

import { describeInstant, isEndless, remaining, type CountdownState, type Remaining } from './shared/countdown.ts'

/** Order is the reading order of the clock, largest first. */
const UNITS = [
  { key: 'years', singular: 'Year', plural: 'Years' },
  { key: 'months', singular: 'Month', plural: 'Months' },
  { key: 'days', singular: 'Day', plural: 'Days' },
  { key: 'hours', singular: 'Hour', plural: 'Hours' },
  { key: 'minutes', singular: 'Minute', plural: 'Minutes' },
  { key: 'seconds', singular: 'Second', plural: 'Seconds' }
] as const

type UnitKey = (typeof UNITS)[number]['key']

const FOREVER = 'No countdown — this one runs forever.'

// Distinct from elapsed (-1), but stable so polling never repeats the announcement.
const ENDLESS_MINUTE = -2

interface Cell {
  value: HTMLElement
  label: HTMLElement
  singular: string
  plural: string
}

export interface Display {
  render(now: number, state: CountdownState): void
}

export function mountDisplay(clock: HTMLElement, announcer: HTMLElement): Display {
  const cells = new Map<UnitKey, Cell>()
  for (const unit of UNITS) {
    const value = clock.querySelector<HTMLElement>(`[data-value="${unit.key}"]`)
    const label = clock.querySelector<HTMLElement>(`[data-label="${unit.key}"]`)
    if (!value || !label) throw new Error(`The markup is missing the "${unit.key}" cell.`)
    cells.set(unit.key, { value, label, singular: unit.singular, plural: unit.plural })
  }

  let lastAnnouncedMinute: number | null = null
  let lastDescription = ''

  return {
    render(now, state) {
      // Never calculate against the unused target, even after that date passes.
      const left = isEndless(state) ? null : remaining(now, state.target)
      clock.dataset.forever = left === null ? 'true' : 'false'

      if (left) {
        for (const unit of UNITS) {
          const cell = cells.get(unit.key)!
          const amount = left[unit.key]
          // Padded to two digits so the row never reflows as a number crosses 9.
          // Combined with `font-variant-numeric: tabular-nums` in the stylesheet
          // this makes the whole clock geometrically still while it ticks - the
          // first version jittered every second and it was maddening.
          const text = String(amount).padStart(2, '0')
          if (cell.value.textContent !== text) cell.value.textContent = text
          const label = amount === 1 ? cell.singular : cell.plural
          if (cell.label.textContent !== label) cell.label.textContent = label
        }
      }

      clock.dataset.elapsed = left?.elapsed ? 'true' : 'false'
      const summary = left ? summarise(left) : FOREVER
      clock.setAttribute('aria-label', summary)

      // The target date used to sit under the title as a caption. It was the
      // only line on the page that never changed, and on a page whose whole
      // point is one enormous number it read as clutter, so it moved to a
      // tooltip: still there when you want it, absent when you do not.
      //
      // Deliberately NOT `aria-label` on the clock - that is the live summary a
      // screen reader reads, and overwriting it with a fixed date would trade a
      // useful announcement for a useless one. `title` is announced in addition.
      const description = left ? describe(state, left) : `${FOREVER} Pick a button below to start one.`
      if (description !== lastDescription) {
        clock.title = description
        lastDescription = description
      }

      const minute = left === null ? ENDLESS_MINUTE : left.elapsed ? -1 : Math.floor(left.totalMs / 60_000)
      if (minute !== lastAnnouncedMinute) {
        lastAnnouncedMinute = minute
        announcer.textContent = summary
      }
    }
  }
}

function describe(state: CountdownState, remaining: Remaining): string {
  if (state.mode === 'zero') return 'Countdown ended early. Pick a button below to start it again.'
  if (remaining.elapsed) return `The countdown reached zero on ${describeInstant(state.target)}.`
  const target = describeInstant(state.target)
  return state.mode === 'fresh' ? `A fresh six years, ending ${target}` : `Six years, ending ${target}`
}

/** "5 years, 11 months, 16 days, 3 hours and 41 minutes remaining." */
function summarise(remaining: Remaining): string {
  if (remaining.elapsed) return 'The countdown is at zero.'
  const said: string[] = []
  for (const unit of UNITS) {
    // Seconds are left out on purpose: this string is read aloud, and by the
    // time a screen reader reaches the end of it the seconds are wrong.
    if (unit.key === 'seconds') continue
    const amount = remaining[unit.key]
    if (amount === 0 && said.length === 0) continue
    said.push(`${amount} ${amount === 1 ? unit.singular.toLowerCase() : unit.plural.toLowerCase()}`)
  }
  if (said.length === 0) return 'Under a minute remaining.'
  const last = said.pop()!
  const phrase = said.length > 0 ? `${said.join(', ')} and ${last}` : last
  return `${phrase} remaining.`
}
